import { buildSeoScoreContext, type SeoScoreContext } from './context';
import type { SeoRule } from './rule';
import { BRIEF_RULES } from './rules/brief.rules';
import { IMAGES_RULES } from './rules/images.rules';
import { KEYWORD_RULES } from './rules/keyword.rules';
import { LENGTH_META_RULES } from './rules/length-meta.rules';
import { LINKS_RULES, linkCounts } from './rules/links.rules';
import { computeReadability, READABILITY_RULES } from './rules/readability.rules';
import { STRUCTURE_RULES } from './rules/structure.rules';
import { STYLE_RULES } from './rules/style.rules';
import {
  BLOCKING_RULES,
  BONUS_RULES,
  READING_WORDS_PER_MIN,
  SEO_GRADE_BOUNDS,
  SEO_GROUP_WEIGHTS,
  SEO_RULE_WEIGHTS,
  SEO_SCORE_VERSION,
} from './thresholds';
import {
  SeoRuleStatus,
  SeoScoreGrade,
  SeoScoreGroup,
  type SeoGroupResult,
  type SeoRuleResult,
  type SeoScoreInput,
  type SeoScoreOptions,
  type SeoScoreResult,
  type SeoScoreStats,
} from './types';

/** Ordre d'affichage = ordre des familles de la spec. */
const ALL_RULES: SeoRule[] = [
  ...KEYWORD_RULES,
  ...STRUCTURE_RULES,
  ...LENGTH_META_RULES,
  ...READABILITY_RULES,
  ...LINKS_RULES,
  ...IMAGES_RULES,
  ...STYLE_RULES,
  ...BRIEF_RULES,
];

const GROUP_ORDER: SeoScoreGroup[] = [
  SeoScoreGroup.KEYWORD,
  SeoScoreGroup.STRUCTURE,
  SeoScoreGroup.LENGTH_META,
  SeoScoreGroup.READABILITY,
  SeoScoreGroup.LINKS,
  SeoScoreGroup.IMAGES,
  SeoScoreGroup.STYLE,
  SeoScoreGroup.BRIEF,
];

const STATUS_POINTS: Record<SeoRuleStatus, number> = {
  [SeoRuleStatus.PASS]: 1,
  [SeoRuleStatus.WARN]: 0.5,
  [SeoRuleStatus.FAIL]: 0,
  [SeoRuleStatus.SKIPPED]: 0,
};

export function gradeOf(score: number): SeoScoreGrade {
  if (score >= SEO_GRADE_BOUNDS.EXCELLENT) return SeoScoreGrade.EXCELLENT;
  if (score >= SEO_GRADE_BOUNDS.GOOD) return SeoScoreGrade.GOOD;
  if (score >= SEO_GRADE_BOUNDS.NEEDS_WORK) return SeoScoreGrade.NEEDS_WORK;
  return SeoScoreGrade.POOR;
}

function runRule(rule: SeoRule, ctx: SeoScoreContext, strict: boolean): SeoRuleResult {
  const outcome = rule.run(ctx);
  if (outcome === null) return { key: rule.key, status: SeoRuleStatus.SKIPPED, blocking: false };
  const blocking =
    strict && outcome.status === SeoRuleStatus.FAIL && (BLOCKING_RULES.has(rule.key) || outcome.forceBlocking === true);
  return {
    key: rule.key,
    status: outcome.status,
    value: outcome.value,
    threshold: outcome.threshold,
    blocking,
    locations: outcome.locations && outcome.locations.length > 0 ? outcome.locations : undefined,
  };
}

function scoreGroup(group: SeoScoreGroup, rules: SeoRuleResult[]): SeoGroupResult {
  let earned = 0;
  let possible = 0;
  for (const r of rules) {
    if (r.status === SeoRuleStatus.SKIPPED) continue;
    const w = SEO_RULE_WEIGHTS[r.key] ?? 1;
    const points = STATUS_POINTS[r.status];
    if (BONUS_RULES.has(r.key)) {
      // Bonus : un PASS ajoute des points ET du dénominateur ; un échec est neutre.
      if (r.status === SeoRuleStatus.PASS) {
        earned += w;
        possible += w;
      }
      continue;
    }
    earned += points * w;
    possible += w;
  }
  const evaluable = possible > 0;
  return {
    key: group,
    weight: SEO_GROUP_WEIGHTS[group],
    score: evaluable ? Math.round((earned / possible) * 100) : 0,
    evaluable,
    rules,
  };
}

function buildStats(ctx: SeoScoreContext): SeoScoreStats {
  const r = computeReadability(ctx);
  const links = linkCounts(ctx);
  return {
    words: ctx.wordCount,
    sentences: r.sentences,
    paragraphs: ctx.bodyBlocks.filter((b): boolean => b.type === 'paragraph').length,
    h1: (ctx.input.title !== undefined ? 1 : 0) + ctx.h1InBody.length,
    h2: ctx.headings.filter((h): boolean => h.level === 2).length,
    h3: ctx.headings.filter((h): boolean => h.level === 3).length,
    images: ctx.images.length,
    internal_links: links.internal,
    external_links: links.external,
    reading_time_min: Math.max(1, Math.round(ctx.wordCount / READING_WORDS_PER_MIN)),
    keyword_occurrences: ctx.keywordCount,
    keyword_density: ctx.wordCount === 0 ? 0 : Math.round((ctx.keywordCount / ctx.wordCount) * 1000) / 10,
    avg_sentence_length: Math.round(r.avgSentenceLength * 10) / 10,
    readability: r.readability,
    passive_ratio: Math.round(r.passiveRatio * 1000) / 10,
    transition_ratio: Math.round(r.transitionRatio * 1000) / 10,
  };
}

/**
 * Score SEO d'un article. Pur, synchrone, < 50 ms sur 2 000 mots.
 * Spec : `docs/to_implement/seo/seo-content-score-spec.md`.
 */
export function scoreSeoContent(input: SeoScoreInput, options: SeoScoreOptions = {}): SeoScoreResult {
  const ctx = buildSeoScoreContext(input, options);
  const strict = options.strict === true;
  const results = ALL_RULES.map((rule): SeoRuleResult => runRule(rule, ctx, strict));

  const groups: SeoGroupResult[] = GROUP_ORDER.map((group): SeoGroupResult => {
    const keys = new Set(ALL_RULES.filter((r): boolean => r.group === group).map((r): string => r.key));
    return scoreGroup(
      group,
      results.filter((r): boolean => keys.has(r.key)),
    );
  });

  let weighted = 0;
  let evaluableWeight = 0;
  for (const g of groups) {
    if (!g.evaluable) continue;
    weighted += g.score * g.weight;
    evaluableWeight += g.weight;
  }
  const score = evaluableWeight === 0 ? 0 : Math.round(weighted / evaluableWeight);

  return {
    version: SEO_SCORE_VERSION,
    score,
    grade: gradeOf(score),
    evaluable_weight: evaluableWeight,
    blocking: results.filter((r): boolean => r.blocking).map((r): SeoRuleResult['key'] => r.key),
    groups,
    stats: buildStats(ctx),
  };
}

/** Passe la gate QA (mode strict) : score ≥ seuil ET aucun bloquant. */
export function passesSeoGate(result: SeoScoreResult, minScore: number): boolean {
  return result.score >= minScore && result.blocking.length === 0;
}
